import { ChevronDown, Menu, Smartphone, X } from 'lucide-react'
import { type RefObject, useEffect, useId, useRef, useState, useSyncExternalStore } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { cn } from '../../lib/cn'
import { type NavItem, primaryNav, routes } from '../../lib/navigation'
import { getScrollState, subscribeToScroll } from '../../lib/scroll-state'
import { setSmoothScrollPaused } from '../../lib/smooth-scroll'
import { Button } from '../ui/Button'
import { buttonClasses } from '../ui/button-variants'
import { Container } from '../ui/Container'
import { LanguageSwitcher } from './LanguageSwitcher'
import { Logo } from './Logo'
import { ThemeToggle } from './ThemeToggle'

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE DESKTOP NAV IS A CAPSULE, AND IT HAD 47 px TO SPEND
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The five links sit inside one `rounded-full` surface and the current page is
 * a FILLED PILL inside it rather than a colour change on the text.
 *
 * ⚠️ THE WIDTH IS MEASURED AT EVERY CHANGE, BECAUSE THIS ROW HAS NO ROOM.
 * At 1280 px the container is 1201 px wide. Measured in Chrome, both locales:
 *
 *                        flat row   capsule `p-1`   glass capsule `p-1.5`
 *   EN natural row       1153.7        1155.7            1159.7
 *   EN headroom            47.3          45.3              41.3
 *   NB natural row       1127.5        1129.5            1133.5
 *   NB headroom            73.5          71.5              67.5
 *
 * So the capsule has cost 6 px in total, and it was PAID FOR rather than
 * added: `+8` for `p-1`'s two ends, `+2` for the border, `−8` from taking the
 * item gap from `gap-1` to `gap-0.5`, then `+4` for the reference's `p-1.5`.
 *
 * ⚠️ THE PER-LINK PADDING STAYS AT `px-3`, AND THE REFERENCE'S `px-4` IS NOT
 * AVAILABLE TO US. `px-4` is +40 px across five items against 41.3 px of
 * English headroom — it would land within a pixel of pushing the primary CTA
 * off the edge at the exact breakpoint the desktop nav appears. Roomier
 * padding in the CONDENSED state only was considered and rejected for a
 * second reason: growing horizontal padding on scroll is a layout animation,
 * and every link in the row would visibly jump. The capsule reads as glass
 * because of the translucency, the blur, the shadow and the hairline border —
 * not because of four pixels either side of each label.
 *
 * ⚠️ IF A LINK IS ADDED, OR A LABEL RETRANSLATED, MEASURE AGAIN IN BOTH
 * LOCALES AT 1280 px. English is the longer one HERE (Norwegian's labels are
 * shorter in this particular set), so measure both — the assumption that
 * Norwegian is always the binding locale is wrong for this row.
 *
 * ⚠️ AND IF THE CAPSULE'S PADDING CHANGES, `--header-h` IN globals.css HAS TO
 * MOVE WITH IT. `p-1` → `p-1.5` made the capsule the tallest thing in the row
 * and took the header from 79 px to 83 px; the hero's full-screen height is
 * computed from that constant and would have overhung the fold.
 */
const desktopLinkClasses = ({ isActive }: { isActive: boolean }): string =>
  cn(
    'relative touch-target inline-flex items-center rounded-full px-3 type-body font-medium',
    'transition-colors duration-hover ease-out',
    /*
     * ⚠️ THE ACTIVE STATE IS NOT COLOUR ALONE, AND MUST NOT BECOME IT.
     * It is a filled surface, elevation and the accent bar below — all from
     * theme tokens so the contrast holds in dark mode as well — and `NavLink`
     * still emits `aria-current="page"`, which is what actually announces the
     * current page. The paint is for everyone else.
     */
    isActive
      ? 'bg-card text-foreground shadow-1 ring-1 ring-border'
      : 'text-muted-foreground hover:bg-card/60 hover:text-foreground',
  )

/**
 * The small accent bar under the active item, from the reference nav.
 *
 * It is the third signal on top of the fill and the ring, which is what makes
 * the active state survive a reader who cannot separate the two surfaces —
 * and it is the one signal that is a SHAPE rather than a tint.
 *
 * `bottom-1.5` rather than the reference's `bottom-0`: our pill is a 44 px
 * `touch-target` with a fully round end, so a bar pinned to the very bottom
 * sits on the curve and clips. `aria-hidden`, because `aria-current` already
 * says this.
 */
const ActiveBar = () => (
  <span
    aria-hidden="true"
    className="absolute bottom-1.5 left-1/2 h-0.5 w-4 -translate-x-1/2 rounded-full bg-primary"
  />
)

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE CAPSULE IS GLASS, AND IT IS THE ONLY BLURRED THING ON A PHONE (NONE)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Translucent surface + `backdrop-blur` + a soft outer shadow + a hairline
 * half-opacity border. That combination is what reads as glass floating ABOVE
 * the page; the previous flat opaque `bg-muted` read as a groove cut INTO it.
 *
 * ⚠️ THE ALPHA IS NOT THE SAME IN BOTH THEMES, AND IT CANNOT BE. The
 * reference is `bg-white/80`, which is a light-theme value: inverted for dark
 * it would be a dark film over a dark page and the capsule would disappear.
 * Dark glass has to be the LIGHTER of the two surfaces, so the dark theme uses
 * `muted` (#111C33) over the near-black page rather than `card` (#0D1426),
 * which is darker than the background it sits on.
 *
 * Light: `card/70` (#FFFFFF at 70 %) over a pale page.
 * Dark:  `muted/60` (#111C33 at 60 %) over a near-black page.
 *
 * ⚠️ AND THE ACTIVE PILL PAIR STILL HAS TO SEPARATE. An earlier version drew
 * `bg-background` pills on a `bg-muted/70` capsule — #F6F7F9 on #F0F4F8, six
 * points apart, effectively invisible. The pill is `card` on both themes and
 * carries `ring-1 ring-border`, which is what does the work in dark mode where
 * the card is the darker of the two and needs an edge rather than a lift.
 *
 * ⚠️ `backdrop-filter` COSTS FRAMES, AND THE GATE IS WHAT PAYS FOR IT. This
 * whole `<nav>` is `hidden xl:block`, so there is NO blurred surface on any
 * phone or tablet — the case where a composited blur over scrolling content
 * actually drops frames. It exists only at ≥ 1280 px.
 *
 * `ring`, not `border`, on the pill: a real border is 2 px of layout and every
 * other item in the row would jump sideways when you navigate.
 *
 * `overflow` stays visible — the focus ring is an `outline` with a positive
 * offset and anything else would clip it, and the Explore panel is a
 * descendant of this list.
 */
const navCapsuleClasses = cn(
  'flex items-center gap-0.5 rounded-full p-1.5',
  'border border-border/50 dark:border-border/70',
  'bg-card/70 dark:bg-muted/60 backdrop-blur-md',
  'shadow-2',
)

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE HEADER CONDENSES ON THE WAY DOWN AND COMES BACK ON THE WAY UP
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The reference nav drops its logo and its one Download button on scroll and
 * leaves the capsule alone, centred, floating over the content.
 *
 * ⚠️ WE CANNOT COPY THAT, BECAUSE OUR ROW IS NOT THEIRS. Beside the capsule we
 * carry the language toggle, the theme toggle, Log in AND Get early access.
 * Hiding all four for the whole of a long page removes the primary CTA and two
 * controls a reader may want at any moment — a worse trade than the tidiness
 * is worth. So the condensed state is bound to DIRECTION rather than to depth:
 * scrolling down (reading) condenses, scrolling up (looking for something)
 * brings the whole header back within one gesture. Nothing is ever more than a
 * flick away, and the effect is kept.
 *
 * Three things make this cheap and safe:
 *
 *   • the scroll reading is NOT this component's. It comes from the shared
 *     store in `lib/scroll-state.ts`, which is one passive, rAF-coalesced
 *     listener for the whole site — the back-to-top rail subscribes to the
 *     same one — and which publishes booleans rather than a position, so a
 *     subscriber re-renders a handful of times per page rather than per frame;
 *   • a 120 px floor and an 8 px hysteresis live in that store, so a trackpad
 *     wobble or a rubber-band at the top cannot make this flap;
 *   • it is disabled outright under `prefers-reduced-motion: reduce`. A header
 *     that changes state as you scroll IS motion, and the calm version of this
 *     feature is not having it. (The rail does NOT take this gate — a control
 *     appearing is not motion, and it is the one thing on the page that saves
 *     a reader a very long scroll.)
 *
 * Direction genuinely needs script: a CSS `scroll()` timeline is a pure
 * function of POSITION and cannot tell up from down. This is the one place in
 * the motion work where JavaScript was unavoidable.
 */
const useCondensedHeader = (): boolean => {
  const { down, atTop } = useSyncExternalStore(subscribeToScroll, getScrollState)
  const [calm, setCalm] = useState(false)

  useEffect(() => {
    const query = window.matchMedia('(prefers-reduced-motion: reduce)')
    const sync = (): void => setCalm(query.matches)
    sync()
    query.addEventListener('change', sync)
    return () => query.removeEventListener('change', sync)
  }, [])

  return down && !atTop && !calm
}

/**
 * Did this element take focus from a KEYBOARD?
 *
 * `:focus-visible` is the browser's own answer to that question and is far
 * better informed than anything we could infer — it accounts for the input
 * modality, for text fields (which always show a caret), and for the user's
 * platform conventions.
 *
 * ⚠️ IT FAILS TOWARDS `true`. An engine that does not know the selector throws
 * `SyntaxError` from `matches()`; answering `true` there keeps the header
 * pinned open, which costs the condensing animation and preserves the
 * accessibility behaviour. The other way round would hide a focused control.
 */
const isKeyboardFocus = (target: EventTarget | null): boolean => {
  if (!(target instanceof Element)) return false
  try {
    return target.matches(':focus-visible')
  } catch {
    return true
  }
}

/**
 * How far the capsule has to move to sit in the middle of the row.
 *
 * ⚠️ IT IS A TRANSFORM, AND IT HAS TO BE. Centring the capsule by changing the
 * row's `justify-content`, or by collapsing the side items' width, is a LAYOUT
 * change — and a layout change that happens during a scroll is a CLS entry,
 * because scrolling does not count as recent user input. A `translateX` is
 * composited and shifts nothing.
 *
 * ⚠️ AND THE CAPSULE CANNOT SIMPLY BE CENTRED ALL THE TIME. Measured at
 * 1280 px: the container is 1201 px, a centred capsule would span 296 → 905,
 * and the right-hand controls need 379 px, i.e. they start at 822. They
 * overlap by 83 px. Even at 1440 the clearance is 4 px. So centred is only
 * available in the state where the sides are not drawn.
 *
 * The measurement uses `offsetLeft`/`offsetWidth`, not `getBoundingClientRect`
 * — those are layout values and are NOT affected by the transform we are about
 * to apply, so re-measuring while condensed cannot feed back on itself. Both
 * are read against `offsetParent`, which is the `relative` Container, so no
 * second ref is needed and `offsetParent === null` doubles as the "the nav is
 * `display: none` below 1280 px" guard. It runs on mount and on resize — a
 * locale change resizes the capsule and the observer catches that too — and
 * never, ever on scroll.
 */
const useCentreOffset = (navRef: RefObject<HTMLElement | null>): number => {
  const [dx, setDx] = useState(0)

  useEffect(() => {
    const nav = navRef.current
    if (!nav) return undefined

    const measure = (): void => {
      const row = nav.offsetParent as HTMLElement | null
      // `hidden xl:block`: below the breakpoint there is no capsule to centre.
      if (!row) {
        setDx(0)
        return
      }
      setDx(Math.round(row.clientWidth / 2 - (nav.offsetLeft + nav.offsetWidth / 2)))
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(nav)
    observer.observe(document.documentElement)
    return () => observer.disconnect()
  }, [navRef])

  return dx
}

/**
 * The "Explore" dropdown.
 *
 * ─── IT OPENS ON HOVER, AND THE LABEL DOES NOT NAVIGATE ────────────────────
 *
 * The developer asked for both, 23 Sep: the menu opens when the pointer is over
 * "Explore Avexa", and "Explore Avexa" itself is not a link.
 *
 * This replaced a `NavLink` to `/categories` with a separate chevron button
 * beside it, on the reasoning that a group label which only opens a panel is a
 * dead end for a crawler. That reasoning is not wrong — but `/categories` is
 * still in the panel, still in the footer and still in the sitemap, so nothing
 * became unreachable, and the product decision is the developer's.
 *
 * ⚠️ HOVER IS AN ADDITION, NEVER THE ONLY WAY IN. A hover-only menu is
 * unreachable by keyboard and does not exist on a touch screen, where there is
 * no hover to enter. So the trigger is a real `<button>` that ALSO toggles on
 * click, Enter and Space, and the hover handlers are gated on
 * `pointerType === 'mouse'` — otherwise a tap would open on the synthetic
 * pointerenter and immediately close on the click that follows it, which is the
 * classic way a touch user gets a menu that flashes and vanishes.
 *
 * ⚠️ AND THE CLOSE IS DELAYED ON PURPOSE. Leaving the trigger to reach the
 * panel means crossing a corner; closing on the first `pointerleave` makes the
 * menu unusable for anyone who does not travel in a straight line. A short
 * grace period, cancelled the moment the pointer arrives anywhere inside the
 * group, is what makes a hover menu feel intentional rather than twitchy. The
 * panel is `mt-1.5` rather than `mt-2` for the same reason: the smaller the gap
 * the less the timer has to cover.
 *
 * It still closes on Escape, on a click outside, and on any navigation.
 */
const ExploreMenu = ({ item }: { item: NavItem }) => {
  const { t } = useTranslation()
  const location = useLocation()
  const [open, setOpen] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const panelId = useId()

  /** The grace period that lets the pointer cross from trigger to panel. */
  const HOVER_CLOSE_MS = 160

  const cancelClose = (): void => {
    if (closeTimer.current !== null) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }

  // A stray timer firing after unmount would call setState on a dead component.
  useEffect(() => cancelClose, [])

  useEffect(() => {
    cancelClose()
    setOpen(false)
  }, [location.pathname])

  useEffect(() => {
    if (!open) return undefined

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setOpen(false)
    }
    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('mousedown', onPointerDown)

    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('mousedown', onPointerDown)
    }
  }, [open])

  /*
   * ⚠️ THE PILL IS ON THE GROUP, NOT ON THE LINK.
   *
   * Explore is two controls — a `NavLink` and the chevron button beside it —
   * and filling only the link would draw half a pill with a chevron hanging
   * off the end of it. So the wrapper carries the surface and the two
   * children carry only the text colour.
   *
   * It is "active" when the current route is `/categories` OR any of the five
   * destinations under it, which is what a grouped nav item means. The link
   * still emits its own `aria-current` when the route is exactly `item.to`;
   * this is the paint, not the announcement.
   */
  const active =
    location.pathname === item.to ||
    (item.children ?? []).some(
      (child) => location.pathname === child.to || location.pathname.startsWith(`${child.to}/`),
    )

  return (
    <div
      ref={containerRef}
      /*
       * On the GROUP, not on the trigger — the panel is inside this element, so
       * moving onto the panel never leaves it and the timer never has to fire.
       * `pointerType` is what keeps touch out: a tap emits `pointerenter` too,
       * and without the guard the menu would open on the tap and close on the
       * click a moment later.
       */
      onPointerEnter={(event) => {
        if (event.pointerType !== 'mouse') return
        cancelClose()
        setOpen(true)
      }}
      onPointerLeave={(event) => {
        if (event.pointerType !== 'mouse') return
        cancelClose()
        closeTimer.current = setTimeout(() => setOpen(false), HOVER_CLOSE_MS)
      }}
      className={cn(
        'relative flex items-center rounded-full transition-colors duration-hover ease-out',
        active ? 'bg-card shadow-1 ring-1 ring-border' : 'hover:bg-card/60',
      )}
    >
      {/*
        ONE control, carrying the label and the chevron. It is a `<button>` and
        not a link because the developer asked that the label not navigate —
        and because a hover menu whose label also navigates is a trap: the
        pointer resting there to read the panel is resting on a link.

        `onClick` stays even though hover opens it. It is the only way in from a
        keyboard (Enter and Space activate a button) and from a touch screen,
        where `pointerType` blocks the hover path entirely.
      */}
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-haspopup="true"
        onClick={() => {
          cancelClose()
          setOpen((value) => !value)
        }}
        className={cn(
          'touch-target inline-flex items-center gap-1 rounded-full pl-3 pr-2 type-body font-medium',
          'transition-colors duration-hover ease-out',
          active ? 'text-foreground' : 'text-muted-foreground',
        )}
      >
        {t(item.labelKey)}
        <ChevronDown
          aria-hidden="true"
          className={cn('size-4 transition-transform duration-hover ease-out', open && 'rotate-180')}
        />
      </button>

      {active ? <ActiveBar /> : null}

      {open ? (
        <ul
          id={panelId}
          className="absolute left-0 top-full z-50 mt-1.5 flex w-60 flex-col gap-0.5 rounded-xl border border-border bg-card p-2 shadow-3"
        >
          {(item.children ?? []).map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                onClick={() => setOpen(false)}
                className={({ isActive }) =>
                  cn(
                    'touch-target flex items-center rounded-lg px-3 type-body font-medium',
                    'transition-colors duration-hover ease-out',
                    isActive
                      ? 'bg-muted text-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                  )
                }
              >
                {t(child.labelKey)}
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * THE MOBILE SHEET GETS A `Home` ROW THAT THE DESKTOP ROW DOES NOT — § 7
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * The client's 29 Sep review § 7, "WEBSITE – NEW PAGES / TABS", lists exactly
 * two: *"Home. Contact Us."* `/contact` became a real page on 6 Oct. `Home`
 * was treated as already satisfied because the route exists — and that reading
 * missed the half he could actually see: **it was in no menu anywhere.** The
 * wordmark links home, which a desktop reader understands and a reader looking
 * at an open side sheet does not; § 3.1 of the same document is him describing
 * that side menu, so the side menu is where the entry belongs.
 *
 * ⚠️ IT IS NOT ADDED TO `primaryNav`, AND THAT IS THE WHOLE POINT OF DOING IT
 * HERE. The desktop row measured 1265 px against a 1280 px breakpoint before
 * the "Explore" dropdown bought the space back; the control pushed off the
 * right edge first is the primary CTA; and Norwegian is the longer locale. A
 * sixth desktop item is precisely the change the warning above `primaryNav` in
 * `lib/navigation.ts` exists for, and it cannot be measured from this machine.
 * The sheet is a vertical list with no such constraint.
 *
 * It is spread rather than pushed so `primaryNav` — which is also the desktop
 * row — is not mutated, and so the demo build's `forDeployment` filtering on it
 * still applies unchanged. `Home` carries no `built` flag because it is not
 * passed through `forDeployment`: the index route is the one destination that
 * cannot be unfinished.
 */
const mobileNav: NavItem[] = [{ to: routes.home, labelKey: 'nav.home' }, ...primaryNav]

export const Header = () => {
  const { t } = useTranslation()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const closeButtonRef = useRef<HTMLButtonElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const navRef = useRef<HTMLElement>(null)
  const scrollingDown = useCondensedHeader()
  const centreDx = useCentreOffset(navRef)

  /*
   * ⚠️ A KEYBOARD USER MUST NEVER BE TABBED INTO A CONTROL THEY CANNOT SEE.
   *
   * The condensed header fades the logo and the four right-hand controls to
   * `opacity: 0` and takes their pointer events away — but leaves them
   * focusable, which on its own is the classic invisible-focus bug. This is
   * what closes it: the moment focus lands anywhere inside the header, the
   * header un-condenses, so tabbing to "Log in" brings it back into view
   * rather than moving an invisible ring around. Same reason the sheet being
   * open pins it open.
   *
   * ⚠️ AND IT HAS TO BE `:focus-visible`, NOT `focus`. THE FIRST VERSION USED
   * PLAIN FOCUS AND BROKE THE HEADER ON EVERY PAGE BUT ONE.
   *
   * Clicking a nav link focuses that `<a>`. So `focusWithin` went true, the
   * router navigated, and focus STAYED on the link — still inside the header,
   * so the blur handler never fired and the flag was pinned `true` for the
   * rest of that page. The header could then never condense again. It looked
   * like "only the home page works", because the home page is the one you
   * arrive at without clicking a nav link.
   *
   * The distinction the code was missing is keyboard focus versus mouse
   * focus, and `:focus-visible` is exactly that distinction. A click no longer
   * pins the header; a Tab still does. `matches(':focus-visible')` is wrapped
   * because an old engine throws `SyntaxError` on a selector it does not know
   * — and it fails towards `true`, which keeps the accessibility guarantee and
   * only costs the condensing.
   *
   * The second half of the fix is the route-change reset below. Even for a
   * keyboard user, focus stays on the link they pressed Enter on, so without
   * it the header would arrive at the new page already pinned open.
   */
  const [focusWithin, setFocusWithin] = useState(false)
  const condensed = scrollingDown && !focusWithin && !menuOpen

  // Any navigation closes the sheet — including back/forward — and releases
  // the focus pin. See the note above: focus stays on the link that was
  // activated, so without this reset the header lands on the new page already
  // held open and can never condense there.
  useEffect(() => {
    setMenuOpen(false)
    setFocusWithin(false)
  }, [location.pathname])

  /*
   * While the sheet is open: lock the page BEHIND it, hand the wheel BACK to
   * the browser, and let Escape dismiss it.
   *
   * ⚠️ THE SECOND OF THOSE THREE IS THE CLIENT'S § 3.1 BUG, AND IT IS NOT THE
   * BODY LOCK. `document.body.style.overflow = 'hidden'` is correct and stays —
   * the page behind a modal sheet must not move. What made the SHEET
   * unscrollable was Lenis: `smoothWheel` cancels every `wheel` event on
   * `window` and moves the document instead, so inside a fixed overlay the
   * wheel reached neither the overlay (cancelled) nor the document (hidden).
   * The only remaining way down was Tab, which is exactly what the client
   * described as *"having to manually navigate down just to reach options such
   * as Login"*.
   *
   * ⚠️ IT CANNOT BE REPRODUCED ON A PHONE. `lib/smooth-scroll.ts` disables
   * Lenis on `(pointer: coarse)`, so touch was always fine; the broken case is
   * a mouse or trackpad in a window narrower than `xl`, which is where this
   * sheet lives and where the client was. Test both.
   *
   * `setSmoothScrollPaused` is a no-op when Lenis is not running (touch,
   * reduced motion), so there is nothing to guard here.
   */
  useEffect(() => {
    if (!menuOpen) return undefined

    const previousOverflow = document.body.style.overflow
    const trigger = triggerRef.current
    document.body.style.overflow = 'hidden'
    setSmoothScrollPaused(true)
    closeButtonRef.current?.focus()

    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') setMenuOpen(false)
    }

    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.body.style.overflow = previousOverflow
      setSmoothScrollPaused(false)
      document.removeEventListener('keydown', onKeyDown)
      // Return focus to the trigger so keyboard users do not lose their place.
      trigger?.focus()
    }
  }, [menuOpen])

  /*
    The sheet is portalled to <body> on purpose. The header uses
    `backdrop-blur`, and a backdrop-filter makes an element the containing block
    for its `position: fixed` descendants — a sheet rendered inside the header
    would be clipped to the header's own box instead of covering the viewport.
  */
  const renderMobileSheet = () =>
    createPortal(
      <div
        id="mobile-menu"
        role="dialog"
        aria-modal="true"
        aria-label={t('header.mobileMenu')}
        /*
          ⚠️ `data-lenis-prevent` IS LOAD-BEARING, NOT DECORATION.

          It is Lenis's own opt-out: wheel and touch events whose target has an
          ancestor carrying this attribute are left alone, so this overlay
          scrolls natively while the page behind it stays locked. Without it the
          menu cannot be scrolled by a mouse at all (the client's § 3.1) —
          `setSmoothScrollPaused` in the effect above is the belt to this
          braces, and both are kept because they fail in opposite directions:
          the attribute survives a refactor of the effect, and the call
          survives a refactor of this markup.
        */
        data-lenis-prevent
        className="fixed inset-0 z-50 flex flex-col overflow-y-auto overscroll-contain bg-background xl:hidden"
      >
        {/*
          Sticky, so Close never scrolls away from someone part-way down a long
          menu. `shrink-0` because this row and the CTA block below must keep
          their own height when the nav between them overflows — a flex item
          with `min-height: auto` will not shrink below its content, but saying
          so is cheaper than relying on it.
        */}
        <div className="sticky top-0 z-10 flex shrink-0 items-center justify-between gap-4 border-b border-border bg-background px-4 py-3 sm:px-6">
          <Logo />
          <Button
            ref={closeButtonRef}
            variant="ghost"
            size="sm"
            className="px-3"
            onClick={() => setMenuOpen(false)}
          >
            <X aria-hidden="true" className="size-5" />
            <span className="sr-only">{t('header.closeMenu')}</span>
          </Button>
        </div>

        <nav aria-label={t('header.mobileMenu')} className="flex-1 px-4 py-6 sm:px-6">
          {/*
            The sheet has no width problem, so "Explore" is not a dropdown here
            — it is a heading with its destinations listed under it. A menu
            inside a menu is two taps to reach a link that fits on the screen
            anyway.

            ⚠️ AND IT HAS NO WIDTH PROBLEM, WHICH IS WHY `Home` IS HERE AND NOT
            IN `primaryNav` — see `mobileNav` above.
          */}
          <ul className="flex flex-col gap-1">
            {mobileNav.map((item) => (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  /* `/` matches every path unless the match is exact, so
                     without this the Home row is highlighted on every page. */
                  end={item.to === routes.home}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    cn(
                      'touch-target flex items-center rounded-lg px-4 type-h4',
                      'transition-colors duration-hover ease-out',
                      isActive
                        ? 'bg-muted text-foreground'
                        : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  {t(item.labelKey)}
                </NavLink>

                {item.children ? (
                  <ul className="mb-2 ml-4 flex flex-col gap-1 border-l border-border pl-3">
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <NavLink
                          to={child.to}
                          onClick={() => setMenuOpen(false)}
                          className={({ isActive }) =>
                            cn(
                              'touch-target flex items-center rounded-lg px-4 type-body font-medium',
                              'transition-colors duration-hover ease-out',
                              isActive
                                ? 'bg-muted text-foreground'
                                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
                            )
                          }
                        >
                          {t(child.labelKey)}
                        </NavLink>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </li>
            ))}
          </ul>
        </nav>

        <div className="flex shrink-0 flex-col gap-3 border-t border-border px-4 py-6 sm:px-6">
          <Link to={routes.download} className={buttonClasses('primary', 'lg', 'w-full')}>
            <Smartphone aria-hidden="true" className="size-5" />
            {t('header.earlyAccess')}
          </Link>
          <Link to={routes.login} className={buttonClasses('outline', 'lg', 'w-full')}>
            {t('header.login')}
          </Link>

          <div className="mt-3 flex flex-col gap-3">
            <LanguageSwitcher block />
            <ThemeToggle expanded />
          </div>
        </div>
      </div>,
      document.body,
    )

  return (
    <>
      {/*
        ⚠️ THE HEADER'S BOX NEVER CHANGES SIZE, IN EITHER STATE.

        Condensing fades the bar's surface and its two side groups; it does not
        change the height, remove anything from flow or alter a single box.
        `border-transparent` rather than no border for exactly that reason — a
        dropped 1 px border is a 1 px shift of the entire page.
      */}
      <header
        onFocusCapture={(event) => {
          if (isKeyboardFocus(event.target)) setFocusWithin(true)
        }}
        onBlurCapture={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null)) {
            setFocusWithin(false)
          }
        }}
        className={cn(
          'sticky top-0 z-50 border-b',
          'transition-[background-color,border-color,backdrop-filter] duration-300 ease-out',
          condensed
            ? 'border-transparent bg-transparent backdrop-blur-none'
            : 'border-border bg-background/85 backdrop-blur-md supports-[backdrop-filter]:bg-background/70',
        )}
      >
        <Container className="relative flex items-center justify-between gap-4 py-3">
          <Link
            to={routes.home}
            aria-label={t('a11y.homeLink')}
            className={cn(
              'shrink-0 rounded-md py-1 transition-opacity duration-300 ease-out',
              condensed && 'pointer-events-none opacity-0',
            )}
          >
            <Logo />
          </Link>

          {/*
            ⚠️ THE DESKTOP NAV APPEARS AT `xl` (1280 px), NOT `lg` (1024 px).

            It used to be SIX flat links, and measured 1265 px against the
            1280 px breakpoint in Norwegian — fifteen pixels of headroom, which
            is why `/how-it-works` could not be added even though the client's
            own nav opens with it. At `lg` the same row was 1066 px inside a
            1024 px viewport and clipped the primary CTA off the right edge.

            It is now FIVE items, one of which is a menu (`lib/navigation.ts`),
            which is the client's own structure and is what paid for
            "How it works" and "About". A 1024–1279 px window still gets the
            full-screen sheet — it holds every link comfortably and is what a
            tablet wants anyway.

            IF A LINK IS EVER ADDED, MEASURE AGAIN, IN BOTH LOCALES. The
            control that goes over the edge is the CTA, and the capsule's own
            width budget is worked out above `desktopLinkClasses`.

            ⚠️ AND A LABEL GETTING LONGER COSTS THE SAME AS A LINK BEING ADDED.
            The rename to Proffplassen did exactly that on 6 Oct 2026: "Utforsk
            {{product}}" went from 13 characters to 20 and the capsule wrapped
            to two lines at 1280 px in Norwegian. The label dropped the product
            name; the measurement is recorded at `nav.explore` in
            `lib/navigation.ts`. The lockup to the left is ~145 px wide and that
            is part of the same budget.
          */}
          {/*
            The capsule is the one thing that stays. `translateX` to the row's
            centre when condensed — a transform, never a `justify-content` or a
            width change, because either of those would be a layout shift
            happening during a scroll, which CLS counts in full.
          */}
          <nav
            ref={navRef}
            aria-label={t('header.primaryNav')}
            className="hidden transition-transform duration-300 ease-out xl:block"
            style={condensed ? { transform: `translateX(${centreDx}px)` } : undefined}
          >
            <ul className={navCapsuleClasses}>
              {primaryNav.map((item) => (
                <li key={item.to}>
                  {item.children ? (
                    <ExploreMenu item={item} />
                  ) : (
                    <NavLink to={item.to} className={desktopLinkClasses}>
                      {({ isActive }) => (
                        <>
                          {t(item.labelKey)}
                          {isActive ? <ActiveBar /> : null}
                        </>
                      )}
                    </NavLink>
                  )}
                </li>
              ))}
            </ul>
          </nav>

          {/*
            ⚠️ `opacity-0` AND `pointer-events-none`, NOT `invisible` OR
            `hidden`. These four controls stay in the layout (so nothing moves)
            and stay FOCUSABLE — and the header's `onFocusCapture` above brings
            them back the instant a keyboard reaches them. `visibility: hidden`
            would drop them from the tab order and a keyboard user would simply
            lose the primary CTA for the whole of a long page.
          */}
          <div
            className={cn(
              'flex items-center gap-2 transition-opacity duration-300 ease-out',
              condensed && 'pointer-events-none opacity-0',
            )}
          >
            <div className="hidden items-center gap-2 xl:flex">
              <LanguageSwitcher />
              <ThemeToggle />
              <Link to={routes.login} className={buttonClasses('ghost', 'sm')}>
                {t('header.login')}
              </Link>
              {/*
                ⚠️ "Get early access", the client's own label, pointing at
                `/download` — the page whose entire job is "the app is not out
                yet, here is what is coming and how you will get it". It
                REPLACES the old "Get the app" rather than sitting beside it:
                two controls to the same URL is noise, and their header has one.

                IT COLLECTS NOTHING, ON PURPOSE. Their render shows an email
                capture and a confirmation toast. There is no table for the
                addresses, no decision about who may read them and no consent
                text — and under the GDPR that is a decision, not a form.
                `Docs/15` N28 asks it. Do not add an input here until it is
                answered.

                The phone mark stays: it is still an app the button is about.
              */}
              <Link to={routes.download} className={buttonClasses('primary', 'sm')}>
                <Smartphone aria-hidden="true" className="size-4" />
                {t('header.earlyAccess')}
              </Link>
            </div>

            <Button
              ref={triggerRef}
              variant="outline"
              size="sm"
              className="px-3 xl:hidden"
              aria-expanded={menuOpen}
              aria-controls="mobile-menu"
              aria-haspopup="dialog"
              onClick={() => setMenuOpen(true)}
            >
              <Menu aria-hidden="true" className="size-5" />
              <span className="sr-only">{t('header.openMenu')}</span>
            </Button>
          </div>
        </Container>
      </header>

      {/* Rendered only while open, so it is fully out of the tab order otherwise. */}
      {menuOpen ? renderMobileSheet() : null}
    </>
  )
}
